import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { cn } from '../../lib/utils';
import { MODAL_SCRIM_CLASS } from '../ui/modalScrim';
import { isNarrowViewport } from './useSidebarCollapse';

// Layers that own Escape themselves (Radix dialogs, popovers, menus, selects).
const OPEN_LAYER_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]:not(:has([role="tooltip"]))';

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keeps Tab inside `container` while the overlay is open: wraps at both ends and
// pulls focus back in when it sits outside.
function wrapTab(e: KeyboardEvent, container: HTMLElement | null) {
  if (!container) return;
  const items = Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
    (el) =>
      !el.closest('[hidden], [inert], [aria-hidden="true"]') &&
      (typeof el.checkVisibility !== 'function' || el.checkVisibility({ visibilityProperty: true })),
  );
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) return;
  const active = document.activeElement;
  if (!active || !container.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

// Open overlays in opening order; only the last one answers Escape.
const openOverlays: symbol[] = [];

/**
 * Below md an expanded rail/nav overlays the content. While it is open on a
 * narrow screen, Escape closes the topmost one (unless another layer already
 * handled the key or is open); `dismiss` (also used by the backdrop) closes it
 * and returns focus to the element `toggleRef` points at, which the caller
 * attaches to the control that re-opens it.
 */
export function useNarrowOverlayDismiss(
  open: boolean,
  onClose: (() => void) | undefined,
): {
  dismiss: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
  trapRef: RefObject<HTMLElement | null>;
} {
  const refocus = useRef(false);
  const trapRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const dismiss = useCallback(() => {
    refocus.current = true;
    onClose?.();
  }, [onClose]);

  // Registration keys on `open` alone: callers pass inline `onClose` closures,
  // and re-registering on every render would reshuffle the stacking order.
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  }, [dismiss]);
  const closable = Boolean(onClose);

  useEffect(() => {
    if (!open || !closable) return;
    const id = Symbol('narrow-overlay');
    openOverlays.push(id);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !isNarrowViewport()) return;
      if (e.key === 'Tab') {
        if (openOverlays[openOverlays.length - 1] !== id || document.querySelector(OPEN_LAYER_SELECTOR)) return;
        wrapTab(e, trapRef.current);
        return;
      }
      if (e.key !== 'Escape') return;
      if (openOverlays[openOverlays.length - 1] !== id) return;
      if (document.querySelector(OPEN_LAYER_SELECTOR)) return;
      dismissRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = openOverlays.indexOf(id);
      if (i !== -1) openOverlays.splice(i, 1);
    };
  }, [open, closable]);

  useEffect(() => {
    if (open) {
      refocus.current = false;
      return;
    }
    if (!refocus.current) return;
    refocus.current = false;
    toggleRef.current?.focus();
  }, [open]);

  return { dismiss, toggleRef, trapRef };
}

/** Click-away scrim for an overlaying rail; only exists visually below md. */
export function NarrowOverlayBackdrop({ onDismiss, className = 'z-[15]' }: { onDismiss: () => void; className?: string }) {
  return (
    <div
      aria-hidden="true"
      data-testid="narrow-overlay-backdrop"
      // The public share view turns clicks outside allowed controls into a login prompt.
      data-public-allowed=""
      onClick={onDismiss}
      className={cn(MODAL_SCRIM_CLASS, 'md:hidden', className)}
    />
  );
}
